import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const MagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, setAuthenticatedUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [error, setError] = useState('');

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 500);
      return;
    }

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setError('No verification token found in the link.');
      return;
    }

    // Verify the magic link token
    const verifyToken = async () => {
      try {
        const response = await api.verifyMagicLink(token);

        if (response.data.success) {
          setStatus('success');

          // Update authentication state in the context
          if (response.data.user && response.data.organization) {
            await setAuthenticatedUser(response.data.user, response.data.organization);

            // Wait a bit longer to ensure context is fully updated
            setTimeout(() => {
              // Force a page reload to ensure all context data is properly loaded
              // This is the safest approach to avoid race conditions
              window.location.href = '/dashboard';
            }, 500);
          } else {
            // If we don't have complete user data, redirect after shorter delay
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 500);
          }
        } else {
          setStatus('error');
          setError(response.data.error || 'Invalid or expired link.');
        }
      } catch (err) {
        console.error('Magic link verification error:', err);
        const errorMessage = err.response?.data?.error || 'Failed to verify magic link.';

        if (errorMessage.includes('expired')) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
        setError(errorMessage);
      }
    };

    verifyToken();
  }, [searchParams, navigate, isAuthenticated, setAuthenticatedUser]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-16 w-16 bg-blue-100 rounded-full">
                <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">Verifying Your Secure Link</h3>
              <p className="text-sm text-gray-600">
                Please wait while we securely sign you in...
              </p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-16 w-16 bg-green-100 rounded-full">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">Welcome Back!</h3>
              <p className="text-sm text-gray-600">
                You've been successfully signed in. Setting up your dashboard...
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm text-green-700">
                <p className="font-medium">✨ Secure Access Granted</p>
                <p className="mt-1">Preparing your Snwolley Business API dashboard...</p>
              </div>
            </div>

            {/* Loading indicator for the setup process */}
            <div className="flex justify-center">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Loading your permissions...</span>
              </div>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-16 w-16 bg-yellow-100 rounded-full">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">Link Expired</h3>
              <p className="text-sm text-gray-600">
                This secure link has expired for security reasons.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-yellow-700">
                <p className="font-medium">⏰ Security Note</p>
                <p className="mt-1">Secure links expire after 15 minutes to keep your account secure.</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-whatsapp-500 to-green-600 hover:from-whatsapp-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Get New Secure Link
            </button>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex items-center justify-center h-16 w-16 bg-red-100 rounded-full">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">Sign-In Failed</h3>
              <p className="text-sm text-gray-600">
                {error || 'There was a problem with your secure link.'}
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-sm text-red-700">
                <p className="font-medium">🔒 What went wrong?</p>
                <p className="mt-1">The link may be invalid, expired, or already used.</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-whatsapp-500 to-green-600 hover:from-whatsapp-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Try Again
            </button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-whatsapp-50 via-green-50 to-emerald-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex items-center justify-center h-16 w-16 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-2xl shadow-lg">
              <ChatBubbleBottomCenterTextIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Snwolley Business API
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Secure link authentication
          </p>
        </div>

        {/* Content */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Powered by Snwolley Business API Platform
          </p>
          <p className="text-xs text-gray-400 mt-1">
            © 2025 Snwolley. All rights reserved.
          </p>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-whatsapp-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 -right-20 w-60 h-60 bg-green-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-emerald-200 rounded-full opacity-20 animate-pulse delay-2000"></div>
      </div>
    </div>
  );
};

export default MagicLink;